/**
 * /onboarding — Demand-first waitlist application.
 *
 * Light, premium experience matching Welcome / Login / Account /
 * Checkout. No dark city skyline, no navy overlays. Cohesive Link
 * design system end-to-end so the rider funnel feels like one
 * transportation product.
 *
 * Flow:
 *   Step 1 of 5 — Where do you travel?       (pickup + destination)
 *   Step 2 of 5 — When do you need transportation? (pickup + return windows)
 *   Step 3 of 5 — How often do you ride?     (day selection)
 *   Step 4 of 5 — Tell us about yourself     (name, email, phone, password)
 *   Step 5 of 5 — Review your request
 *
 * Backend contract unchanged — POST /api/auth/waitlist-apply accepts the
 * same data shape (frequency derived from day-count so existing
 * validation continues to pass).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, Loader2, Eye, EyeOff, Clock, Check } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/shared/context/AuthContext";
import { api, formatApiError } from "@/shared/lib/api";
import { executeRecaptcha as getRecaptchaToken, hideRecaptchaBadge } from "@/shared/lib/recaptcha";
import LightShell, { RouteIllustration } from "@/apps/rider/components/LightShell";
import AddressInput from "@/shared/components/AddressInput";
import WindowSelect, { formatWindowLabel } from "@/apps/rider/components/WindowSelect";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const STEPS = [
  { id: "addresses", label: "Where" },
  { id: "schedule",  label: "When" },
  { id: "frequency", label: "Days" },
  { id: "identity",  label: "You" },
  { id: "review",    label: "Review" },
];

/** Derive a free-text frequency label from the rider's day count so the
 *  backend's existing validation continues to accept the payload. */
function deriveFrequency(daysCount) {
  if (daysCount >= 4) return "Every week";
  if (daysCount >= 2) return "Several times per week";
  if (daysCount === 1) return "Occasionally";
  return "Just exploring";
}

export default function Onboarding() {
  const nav = useNavigate();
  const { user, refreshUser, setUser } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [data, setData] = useState({
    home_address: "",
    destination_address: "",
    days_needed: [],
    pickup_window: "",
    return_window: "",
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    phone: "",
  });

  // Hydrate from /welcome instant-quote preset if present.
  useEffect(() => {
    hideRecaptchaBadge();
    let raw = null;
    try { raw = localStorage.getItem("link.preset.route"); } catch (_) { /* private mode */ }
    if (!raw) return;
    let parsed = null;
    try { parsed = JSON.parse(raw); } catch (_) { /* malformed */ }
    if (!parsed) return;
    setData((d) => ({
      ...d,
      home_address: parsed.pickup || d.home_address,
      destination_address: parsed.destination || d.destination_address,
    }));
  }, []);

  // If already signed in + approved → straight to the map.
  useEffect(() => {
    if (user && user.access_status === "APPROVED") nav("/", { replace: true });
    else if (user && user.access_status === "WAITLIST") nav("/waitlist/status", { replace: true });
  }, [user, nav]);

  const step = STEPS[stepIdx];
  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleDay = (d) => setData((s) => ({
    ...s,
    days_needed: s.days_needed.includes(d)
      ? s.days_needed.filter((x) => x !== d)
      : [...s.days_needed, d],
  }));

  const canAdvance = useMemo(() => {
    switch (step.id) {
      case "addresses":
        return data.home_address.trim().length > 3 && data.destination_address.trim().length > 3;
      case "schedule":
        return !!data.pickup_window && !!data.return_window;
      case "frequency":
        return data.days_needed.length > 0;
      case "identity":
        return (
          data.first_name.trim().length > 0 &&
          data.last_name.trim().length > 0 &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email) &&
          data.password.length >= 8 &&
          data.phone.trim().length >= 7
        );
      case "review":
        return true;
      default: return false;
    }
  }, [step.id, data]);

  const next = () => { if (canAdvance) setStepIdx((i) => Math.min(i + 1, STEPS.length - 1)); };
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const recaptcha_token = await getRecaptchaToken("waitlist_apply").catch(() => null);
      const payload = {
        ...data,
        frequency: deriveFrequency(data.days_needed.length),
        current_method: "",
        recaptcha_token,
      };
      const { data: resp } = await api.post("/auth/waitlist-apply", payload);
      try { localStorage.removeItem("link.preset.route"); } catch (_) { /* noop */ }
      if (resp?.user) setUser?.(resp.user); else await refreshUser?.();
      toast.success("You're on the waitlist");
      nav("/waitlist/status", { replace: true });
    } catch (ex) {
      const msg = formatApiError(ex?.response?.data?.detail) || "Could not submit your application";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LightShell testId="onboarding-page">
      {/* Top row — back chip (when applicable) */}
      <div className="flex items-center justify-between min-h-[20px] mb-3">
        {stepIdx > 0 ? (
          <button
            type="button"
            onClick={back}
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] hover:text-[#ed6500] transition-colors"
            data-testid="onb-back"
          >
            <ChevronLeft size={14} strokeWidth={2} /> Back
          </button>
        ) : <span />}
        <Link
          to="/login"
          className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] hover:text-[#ed6500] transition-colors"
          data-testid="onb-signin-link"
        >
          Sign in
        </Link>
      </div>

      {/* Progress indicator — Step X of 5 + bar */}
      <ProgressBar current={stepIdx} total={STEPS.length} label={step.label} />

      {/* Step content */}
      <div className="mt-7 animate-step-fade" key={step.id}>
        {step.id === "addresses" && (
          <StepFrame
            title="Where do you travel?"
            sub="Tell us where you need transportation so we can prepare service for your route."
          >
            <FieldLabel>Pickup address</FieldLabel>
            <div className="auth-input-wrap">
              <AddressInput
                value={data.home_address}
                onChange={(v) => update("home_address", v)}
                placeholder="Home or pickup location"
                allowCurrentLocation
                showSavedPlaces={false}
                testId="onb-home"
              />
            </div>

            <FieldLabel className="mt-4">Destination</FieldLabel>
            <div className="auth-input-wrap">
              <AddressInput
                value={data.destination_address}
                onChange={(v) => update("destination_address", v)}
                placeholder="Work, school, healthcare, gym, airport…"
                allowCurrentLocation={false}
                showSavedPlaces={false}
                testId="onb-dest"
              />
            </div>

            <div className="mt-6">
              <RouteIllustration
                origin={shortLabel(data.home_address) || "Home"}
                destination={shortLabel(data.destination_address) || "Destination"}
                testId="onb-route-illustration"
              />
            </div>
          </StepFrame>
        )}

        {step.id === "schedule" && (
          <StepFrame
            title="When do you need transportation?"
            sub="Choose the pickup and return times that work best for your schedule."
          >
            <FieldLabel icon={Clock}>Pickup window</FieldLabel>
            <WindowSelect
              value={data.pickup_window}
              onChange={(v) => update("pickup_window", v)}
              placeholder="Choose pickup window…"
              testId="onb-pickup-window"
              variant="light"
            />

            <FieldLabel icon={Clock} className="mt-5">Return window</FieldLabel>
            <WindowSelect
              value={data.return_window}
              onChange={(v) => update("return_window", v)}
              placeholder="Choose return window…"
              testId="onb-return-window"
              variant="light"
            />

            <p className="mt-5 text-[12px] text-[#515E7B] leading-relaxed">
              Link reserves a 15-minute window so your driver always shows up on time — no surge, no guesswork.
            </p>
          </StepFrame>
        )}

        {step.id === "frequency" && (
          <StepFrame
            title="How often do you ride?"
            sub="Select the days you typically need transportation."
          >
            <FieldLabel>Days of the week</FieldLabel>
            <div className="grid grid-cols-7 gap-1.5" data-testid="onb-days">
              {DAYS.map((d) => {
                const active = data.days_needed.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDay(d)}
                    className={`aspect-square rounded-xl text-[13px] font-semibold tracking-wide transition-colors ${
                      active
                        ? "bg-[#ed6500] text-white border border-[#ed6500] shadow-sm"
                        : "bg-white text-[#0A1128] border border-black/10 hover:border-[#ed6500]/60"
                    }`}
                    data-testid={`onb-day-${d}`}
                    aria-pressed={active}
                  >
                    {d.charAt(0)}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 rounded-2xl bg-white border border-black/[0.06] shadow-sm p-4">
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#ed6500]">Selected</div>
              <div className="font-serif text-[18px] text-[#0A1128] mt-1.5" data-testid="onb-days-summary">
                {data.days_needed.length
                  ? `${data.days_needed.length} day${data.days_needed.length === 1 ? "" : "s"} · ${data.days_needed.join(" · ")}`
                  : "Pick the days that fit your week"}
              </div>
            </div>
          </StepFrame>
        )}

        {step.id === "identity" && (
          <StepFrame
            title="Tell us about yourself"
            sub="We'll use this information to keep you updated about your transportation availability."
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>First name</FieldLabel>
                <input
                  type="text"
                  value={data.first_name}
                  onChange={(e) => update("first_name", e.target.value)}
                  placeholder="First"
                  className="auth-input"
                  data-testid="onb-first"
                />
              </div>
              <div>
                <FieldLabel>Last name</FieldLabel>
                <input
                  type="text"
                  value={data.last_name}
                  onChange={(e) => update("last_name", e.target.value)}
                  placeholder="Last"
                  className="auth-input"
                  data-testid="onb-last"
                />
              </div>
            </div>

            <FieldLabel className="mt-4">Email</FieldLabel>
            <input
              type="email"
              value={data.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="you@email.com"
              autoComplete="email"
              className="auth-input"
              data-testid="onb-email"
            />

            <FieldLabel className="mt-4">Phone</FieldLabel>
            <input
              type="tel"
              value={data.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="(555) 123-4567"
              autoComplete="tel"
              className="auth-input"
              data-testid="onb-phone"
            />

            <FieldLabel className="mt-4">Password</FieldLabel>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={data.password}
                onChange={(e) => update("password", e.target.value)}
                placeholder="Create a password (8+ characters)"
                autoComplete="new-password"
                className="auth-input pr-11"
                data-testid="onb-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[#515E7B] hover:text-[#ed6500] transition-colors"
                aria-label={showPw ? "Hide password" : "Show password"}
                data-testid="onb-password-toggle"
              >
                {showPw ? <EyeOff size={16} strokeWidth={1.8} /> : <Eye size={16} strokeWidth={1.8} />}
              </button>
            </div>
          </StepFrame>
        )}

        {step.id === "review" && (
          <ReviewStep data={data} submitting={submitting} onSubmit={submit} />
        )}
      </div>

      {/* Footer nav — primary CTA. Hidden on review (its own submit button). */}
      {step.id !== "review" && (
        <div className="mt-10">
          <button
            type="button"
            onClick={next}
            disabled={!canAdvance}
            className="group w-full h-14 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] active:bg-[#d75600] disabled:opacity-40 disabled:cursor-not-allowed text-white text-[13px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-2 transition-colors"
            data-testid="onb-next"
          >
            Continue
            <ArrowRight size={14} strokeWidth={2.2} className="transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      )}

      {stepIdx === 0 && (
        <p className="text-[12px] text-[#515E7B] mt-6 text-center">
          Already approved? <Link to="/login" className="font-semibold text-[#ed6500] hover:underline">Sign in</Link>
        </p>
      )}

      {/* Light-theme input styles + subtle step fade + one-pass route pulse. */}
      <style>{`
        .auth-input {
          width: 100%;
          background: #FFFFFF;
          color: #0A1128;
          padding: 14px 14px;
          border: 1px solid rgba(10,17,40,0.10);
          border-radius: 12px;
          font-size: 15px;
          outline: none;
          transition: border-color 180ms, box-shadow 180ms;
        }
        .auth-input::placeholder { color: rgba(81,94,123,0.55); }
        .auth-input:focus {
          border-color: #ed6500;
          box-shadow: 0 0 0 4px rgba(237,101,0,0.08);
        }
        /* AddressInput renders its own inner input — wrap it so light-theme
           styles cascade without needing a variant flag. */
        .auth-input-wrap input {
          background: #FFFFFF !important;
          color: #0A1128 !important;
          border: 1px solid rgba(10,17,40,0.10) !important;
          border-radius: 12px !important;
          padding: 14px 14px !important;
          font-size: 15px !important;
          transition: border-color 180ms, box-shadow 180ms !important;
        }
        .auth-input-wrap input::placeholder { color: rgba(81,94,123,0.55) !important; }
        .auth-input-wrap input:focus {
          border-color: #ed6500 !important;
          box-shadow: 0 0 0 4px rgba(237,101,0,0.08) !important;
          outline: none !important;
        }

        @keyframes onb-step-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-step-fade { animation: onb-step-in 360ms cubic-bezier(0.22, 1, 0.36, 1); }

        @keyframes onb-route-pulse {
          0%   { transform: scaleX(0); transform-origin: left; opacity: 0.4; }
          60%  { opacity: 1; }
          100% { transform: scaleX(1); transform-origin: left; opacity: 1; }
        }
        [data-testid="onb-route-illustration"] [aria-hidden="true"].h-\\[2px\\] {
          animation: onb-route-pulse 720ms cubic-bezier(0.22, 1, 0.36, 1);
        }
      `}</style>
    </LightShell>
  );
}

/* ============================  bits  ============================ */

function ProgressBar({ current, total, label }) {
  const pct = Math.round(((current + 1) / total) * 100);
  return (
    <div data-testid="onb-progress">
      <div className="flex items-center justify-between text-[10px] tracking-[0.28em] uppercase font-semibold text-[#515E7B]">
        <span data-testid="onb-progress-label">
          Step {current + 1} of {total} · <span className="text-[#ed6500]">{label}</span>
        </span>
        <span className="text-[#515E7B]/70" data-testid="onb-progress-pct">{pct}%</span>
      </div>
      <div className="mt-2 flex items-center gap-1.5" aria-hidden="true">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
              i <= current ? "bg-[#ed6500]" : "bg-black/10"
            }`}
            data-testid={`onb-progress-pip-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

function StepFrame({ title, sub, children }) {
  return (
    <section>
      <div className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ed6500] mb-2" data-testid="onb-eyebrow">
        Your transportation
      </div>
      <h1
        className="font-serif text-[32px] sm:text-[40px] leading-[1.05] tracking-tight text-[#0A1128]"
        data-testid="onb-headline"
      >
        {title}
      </h1>
      {sub && (
        <p className="text-[15px] text-[#515E7B] leading-relaxed mt-3 max-w-prose" data-testid="onb-sub">
          {sub}
        </p>
      )}
      <div className="mt-7 space-y-2">{children}</div>
    </section>
  );
}

function FieldLabel({ children, icon: Icon, className = "" }) {
  return (
    <div className={`mb-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] inline-flex items-center gap-1.5 ${className}`}>
      {Icon && <Icon size={11} strokeWidth={1.8} className="text-[#ed6500]" />}
      {children}
    </div>
  );
}

function ReviewStep({ data, submitting, onSubmit }) {
  const daysStr = data.days_needed.length ? data.days_needed.join(" · ") : "—";
  return (
    <section data-testid="onb-review">
      <div className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ed6500] mb-2">
        Almost there
      </div>
      <h1 className="font-serif text-[32px] sm:text-[40px] leading-[1.05] tracking-tight text-[#0A1128]" data-testid="onb-headline">
        Review your request
      </h1>
      <p className="text-[15px] text-[#515E7B] leading-relaxed mt-3 max-w-prose" data-testid="onb-sub">
        Confirm your route and schedule before joining.
      </p>

      <div className="mt-7 space-y-4">
        {/* Route */}
        <div className="rounded-3xl bg-white border border-black/[0.06] shadow-sm p-5 space-y-3" data-testid="onb-review-route">
          <SectionLabel>Route</SectionLabel>
          <ReviewRow label="Pickup" value={data.home_address} testId="onb-review-pickup" />
          <ReviewRow label="Destination" value={data.destination_address} testId="onb-review-destination" />
        </div>

        {/* Schedule */}
        <div className="rounded-3xl bg-white border border-black/[0.06] shadow-sm p-5 space-y-3" data-testid="onb-review-schedule">
          <SectionLabel>Schedule</SectionLabel>
          <ReviewRow label="Pickup window" value={formatWindowLabel(data.pickup_window)} testId="onb-review-pickup-window" />
          <ReviewRow label="Return window" value={formatWindowLabel(data.return_window)} testId="onb-review-return-window" />
        </div>

        {/* Days */}
        <div className="rounded-3xl bg-white border border-black/[0.06] shadow-sm p-5 space-y-3" data-testid="onb-review-days">
          <SectionLabel>Days selected</SectionLabel>
          <ReviewRow label="Days" value={daysStr} testId="onb-review-days-value" />
        </div>

        {/* Transportation type */}
        <div className="rounded-3xl bg-white border border-black/[0.06] shadow-sm p-5 space-y-3" data-testid="onb-review-type">
          <SectionLabel>Transportation type</SectionLabel>
          <ReviewRow label="Plan" value="Reliable pickup and return transportation" testId="onb-review-plan" />
        </div>
      </div>

      <button
        type="button"
        onClick={onSubmit}
        disabled={submitting}
        className="mt-7 w-full h-14 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] active:bg-[#d75600] disabled:opacity-50 text-white text-[13px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-2 transition-colors"
        data-testid="onb-submit"
      >
        {submitting ? (
          <><Loader2 size={14} className="animate-spin" /> Submitting</>
        ) : (
          <><Check size={14} strokeWidth={2.2} /> Submit application</>
        )}
      </button>
      <p className="text-[11px] text-[#515E7B] mt-3 text-center">
        We&rsquo;ll notify you the moment transportation is available on your route.
      </p>
    </section>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#0A1128]/55">
      {children}
    </div>
  );
}

function ReviewRow({ label, value, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] shrink-0">{label}</div>
      <div className="text-[14px] text-[#0A1128] text-right break-words" data-testid={testId}>{value || "—"}</div>
    </div>
  );
}

/** Short-label helper for RouteIllustration: first comma-separated part. */
function shortLabel(addr) {
  if (!addr) return "";
  return addr.split(",")[0].trim().slice(0, 24);
}
